// Password attribute. The stored value is shown as ciphertext so the field
// renders a mask of plausible length; `encryptText` is injected because each app
// keys it differently.

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import TextField from '@decisiv/ui-components/lib/components/TextField';

import { fieldPropTypes } from './fieldPropTypes';

const asText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
};

const PasswordInput = ({
  descriptor,
  label,
  value,
  warningMessage = '',
  readOnly = true,
  onChange,
  onBlur,
  encryptText,
  maskUntilEdited = false,
}) => {
  const [original, setOriginal] = useState(() => asText(value));
  const [edited, setEdited] = useState(false);

  // Re-sync the snapshot when the stored value changes and the user has not
  // typed — a refetch after save re-renders this component without remounting
  // it, and restore-on-erase must not bring back the previous secret.
  useEffect(() => {
    if (!edited) setOriginal(asText(value));
  }, [value, edited]);

  // CryptoJS salts randomly, so an unmemoized mask would change on every render
  // and jitter the caret.
  const cipher = useMemo(() => encryptText(original), [encryptText, original]);

  const masked = readOnly || (maskUntilEdited && !edited);
  const display = masked ? cipher : asText(value);

  if (descriptor.html_tag_hidden) {
    return (
      <input
        type="hidden"
        name={descriptor.name}
        value={readOnly ? cipher : asText(value)}
        readOnly
      />
    );
  }

  // While the mask is on screen the browser would edit the ciphertext itself —
  // clicking mid-mask and typing yields a half-mutated cipher, which would then
  // be saved as the password. Take the keystroke as the first character of
  // fresh input instead.
  const handleKeyDown = (e) => {
    if (!masked || readOnly) return;

    const printable = e.key.length === 1 && !e.ctrlKey && !e.metaKey;
    const erasing = e.key === 'Backspace' || e.key === 'Delete';
    if (!printable && !erasing) return;

    e.preventDefault();
    setEdited(true);
    onChange(descriptor.name, erasing ? '' : e.key);
  };

  const handlePaste = (e) => {
    if (!masked || readOnly) return;

    e.preventDefault();
    setEdited(true);
    onChange(descriptor.name, e.clipboardData.getData('text'));
  };

  const handleChange = (e) => {
    // Erasing everything restores what was stored, so a cleared field never
    // overwrites the saved secret with an empty string.
    if (maskUntilEdited && e.target.value === '' && original !== '') {
      setEdited(false);
      onChange(descriptor.name, original);
      return;
    }

    setEdited(true);
    onChange(descriptor.name, e.target.value);
  };

  return (
    <TextField
      type="password"
      name={descriptor.name}
      label={label}
      required={descriptor.required}
      helpMessage={descriptor.description}
      warningMessage={warningMessage}
      disabled={readOnly}
      value={display}
      onFocus={(e) => masked && !readOnly && e.target.select()}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onChange={handleChange}
      onBlur={() => onBlur?.(descriptor.name)}
    />
  );
};

PasswordInput.propTypes = {
  ...fieldPropTypes,
  encryptText: PropTypes.func.isRequired,
  // Keep the mask on screen after editing starts, until the user actually types.
  // Screens with a read-only mode reveal the plaintext instead.
  maskUntilEdited: PropTypes.bool,
};

export default PasswordInput;
